import { useEffect, useState } from 'react';
import type { RailMetrics } from '@/v2/ui/scrollRailGeometry';

/**
 * One observer over the scroller, feeding both the rail and a measured column. Two
 * components measuring the same element separately is two answers during a resize, and
 * the one frame where they disagree is the frame that draws a pinned column with no rail.
 *
 * ⚠️ It takes the ELEMENT, not a ref to it, and that is the whole point.
 *
 * The first version took a `RefObject` and keyed its effect on `[scrollerRef, measure]` —
 * both permanently stable, so the effect ran exactly once, on mount, and its first line
 * was `if (!el) return`. `DataTable` early-returns `<EmptyState>` when there are no rows
 * and STAYS MOUNTED, so on every list whose first row is created in place — a new shop
 * adding its first customer — the scroller appeared after that one run and was never
 * measured. `metrics` stayed `{0,0,0,0}` for the life of the page: no rail, no pinned
 * column, and `.h-scroll-silent` hides the native bar, so the table could not be
 * scrolled sideways AT ALL. A callback ref makes the element the dependency, so mounts,
 * unmounts and replacements all re-run it.
 */
export function useTableMetrics(
  scroller: HTMLElement | null,
  columnKeys: string,
  columnSelector = 'thead th:first-child',
): RailMetrics & { columnWidth: number } {
  const [metrics, setMetrics] = useState<RailMetrics & { columnWidth: number }>({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
    columnWidth: 0,
  });

  useEffect(() => {
    if (!scroller) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const head = scroller.querySelector(columnSelector);
      const next: RailMetrics & { columnWidth: number } = {
        scrollLeft: scroller.scrollLeft,
        scrollWidth: scroller.scrollWidth,
        clientWidth: scroller.clientWidth,
        columnWidth: head ? head.getBoundingClientRect().width : 0,
      };
      setMetrics((prev) =>
        prev.scrollLeft === next.scrollLeft &&
        prev.scrollWidth === next.scrollWidth &&
        prev.clientWidth === next.clientWidth &&
        prev.columnWidth === next.columnWidth
          ? prev
          : next,
      );
    };
    // Coalesced to a frame: `scroll` fires per pointermove during a drag, and this
    // state re-renders the whole table.
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };

    measure();
    scroller.addEventListener('scroll', schedule, { passive: true });
    const ro = new ResizeObserver(schedule);
    ro.observe(scroller);
    if (scroller.firstElementChild) ro.observe(scroller.firstElementChild);
    // ⚠️ The selected column gets its OWN observation: a page of rows can redistribute
    // the columns (col 0 goes 392px → 500px) while the scroller's boxes hold still.
    const head = scroller.querySelector(columnSelector);
    if (head) ro.observe(head);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      scroller.removeEventListener('scroll', schedule);
      ro.disconnect();
    };
    // `columnKeys`, because a changed column SET replaces the `th` this observes.
  }, [scroller, columnKeys, columnSelector]);

  return metrics;
}
